import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import {
  RefreshCw,
  Plus,
  Loader2,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Image as ImageIcon,
  Trash2,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn =
  "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

const CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];
const HEADER_TYPES = ["NONE", "TEXT", "IMAGE"];

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const map = {
    APPROVED: { cls: "text-green-400 bg-green-500/10", Icon: CheckCircle2 },
    PENDING: { cls: "text-amber-400 bg-amber-500/10", Icon: Clock },
    REJECTED: { cls: "text-red-400 bg-red-500/10", Icon: XCircle },
  };
  const { cls, Icon } = map[s] || { cls: "text-slate-300 bg-white/10", Icon: Clock };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase font-bold rounded px-2 py-0.5 ${cls}`}>
      <Icon className="h-3 w-3" /> {s || "—"}
    </span>
  );
}

function countVars(text) {
  const m = (text || "").match(/{{\s*\d+\s*}}/g);
  return m ? m.length : 0;
}

const emptyForm = {
  name: "",
  language: "en",
  category: "UTILITY",
  body_text: "",
  header_type: "NONE",
  header_text: "",
  footer_text: "",
};

function TemplatePreview({ form, samples, imageUrl, buttons }) {
  const body = (form.body_text || "").replace(/\{\{(\d+)\}\}/g, (m, n) => samples[Number(n) - 1] || m);
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div data-testid="template-preview" className="mx-auto w-[290px] rounded-[2.2rem] border-[6px] border-slate-700 bg-black shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 pt-2 pb-1 bg-[#202c33] text-[10px] text-slate-300">
        <span>{time}</span>
        <span className="h-3 w-16 rounded-full bg-black" />
        <span>5G ▮▮▮</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 bg-[#202c33] border-b border-black/30">
        <span className="text-slate-300 text-sm">‹</span>
        <span className="h-7 w-7 rounded-full bg-orange-500 flex items-center justify-center text-xs font-bold text-white">A</span>
        <div className="leading-tight">
          <p className="text-sm font-semibold text-slate-100">Adiyash Gym</p>
          <p className="text-[10px] text-slate-400">Business account</p>
        </div>
      </div>
      <div className="bg-[#0b141a] p-3 min-h-[360px]">
      <div className="max-w-[240px] rounded-lg rounded-tl-none bg-[#202c33] text-slate-100 shadow overflow-hidden">
        {form.header_type === "IMAGE" && (
          imageUrl
            ? <img src={imageUrl} alt="header" className="w-full h-32 object-cover" />
            : <div className="w-full h-32 bg-white/5 flex items-center justify-center text-xs text-slate-500">Header image</div>
        )}
        <div className="px-3 py-2">
          {form.header_type === "TEXT" && form.header_text && (
            <p className="text-sm font-bold mb-1 break-words">{form.header_text}</p>
          )}
          <p className="text-sm whitespace-pre-wrap break-words">
            {body || <span className="text-slate-500">Your message will appear here</span>}
          </p>
          {form.footer_text && <p className="text-xs text-slate-400 mt-2 break-words">{form.footer_text}</p>}
          <p className="text-[10px] text-slate-500 text-right mt-1">{time}</p>
        </div>
        {buttons.filter((b) => b.text).map((b, i) => (
          <div key={i} className="border-t border-white/10 py-2 text-center text-sm font-medium text-sky-400">
            {b.type === "URL" ? "↗ " : "↩ "}{b.text}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

export default function Templates() {
  const { activeBranchId } = useBranch();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [samples, setSamples] = useState([]);
  const [saving, setSaving] = useState(false);

  const [headerImagePreview, setHeaderImagePreview] = useState("");
  const [headerImageHandle, setHeaderImageHandle] = useState("");
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);

  const [buttons, setButtons] = useState([]);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editFooter, setEditFooter] = useState("");
  const [editSamples, setEditSamples] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/templates", { params: { branch_id: activeBranchId } });
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const n = countVars(form.body_text);
    setSamples((prev) => {
      const next = [...prev];
      next.length = n;
      return Array.from({ length: n }, (_, i) => next[i] || "");
    });
  }, [form.body_text]);

  useEffect(() => {
    const n = countVars(editBody);
    setEditSamples((prev) => {
      const next = [...prev];
      next.length = n;
      return Array.from({ length: n }, (_, i) => next[i] || "");
    });
  }, [editBody]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/templates/sync", { branch_id: activeBranchId });
      toast.success(`Synced ${data.synced} template(s) from Meta`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSamples([]);
    setHeaderImagePreview("");
    setHeaderImageHandle("");
    setButtons([]);
  };

  const onHeaderImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeaderImagePreview(URL.createObjectURL(file));
    setUploadingHeaderImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/templates/header-image", fd, {
        params: { branch_id: activeBranchId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderImageHandle(data.handle);
      toast.success("Header image uploaded to Meta");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Header image upload failed");
      setHeaderImagePreview("");
    } finally {
      setUploadingHeaderImage(false);
    }
  };

  const addButton = (type) => {
    if (buttons.length >= 3) {
      toast.error("Maximum 3 buttons per template.");
      return;
    }
    setButtons([...buttons, { type, text: "", url: type === "URL" ? "" : undefined }]);
  };

  const updateButton = (index, patch) => {
    setButtons(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (form.header_type === "IMAGE" && !headerImageHandle) {
      toast.error("Upload a header image first, or switch header type to None/Text.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/templates", {
        branch_id: activeBranchId,
        name: form.name,
        language: form.language,
        category: form.category,
        body_text: form.body_text,
        sample_values: samples,
        header_type: form.header_type,
        header_text: form.header_type === "TEXT" ? form.header_text : null,
        header_media_handle: form.header_type === "IMAGE" ? headerImageHandle : null,
        footer_text: form.footer_text || null,
        buttons,
      });
      if (data.meta_error) {
        toast.warning(`Saved locally. Meta: ${data.meta_error}`);
      } else {
        toast.success("Template submitted for approval");
      }
      setOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setEditBody(t.body_text || "");
    setEditFooter(t.footer_text || "");
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const { data } = await api.put(`/templates/${editTarget.id}`, {
        body_text: editBody,
        sample_values: editSamples,
        footer_text: editFooter || null,
      });
      if (data.meta_error) {
        toast.warning(`Saved locally. Meta: ${data.meta_error}`);
      } else {
        toast.success("Template updated — may need re-approval from Meta");
      }
      setEditOpen(false);
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/templates/${deleteTarget.id}`);
      if (data.meta_error) {
        toast.warning(`Removed locally. Meta: ${data.meta_error}`);
      } else {
        toast.success("Template deleted");
      }
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="templates-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Templates</h1>
          <p className="text-sm text-slate-500 mt-2">WhatsApp message templates for this branch.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sync-templates-button" className={secondaryBtn} onClick={sync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync from Meta
          </button>
          <button data-testid="create-template-button" className={primaryBtn} onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Template
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-lg flex flex-col items-center justify-center py-20 text-center">
          <FileText className="h-8 w-8 text-orange-600" />
          <p className="mt-3 font-heading text-lg font-bold text-white">No templates yet</p>
          <p className="text-sm text-slate-500 mt-1">Sync from Meta or create your first template.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <p className="font-heading font-bold text-white break-all">{t.name}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={t.status} />
                  <button
                    data-testid={`edit-template-${t.id}`}
                    className="text-slate-400 hover:text-orange-600"
                    onClick={() => openEdit(t)}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`delete-template-${t.id}`}
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => setDeleteTarget(t)}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] tracking-[0.12em] uppercase font-bold text-orange-600 bg-orange-500/10 rounded px-2 py-0.5">{t.category}</span>
                <span className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 bg-white/10 rounded px-2 py-0.5">{t.language}</span>
                {t.header_type && t.header_type !== "NONE" && (
                  <span className="text-[10px] tracking-[0.12em] uppercase font-bold text-blue-400 bg-blue-500/10 rounded px-2 py-0.5">{t.header_type} header</span>
                )}
              </div>
              <div className="mt-4 flex-1 bg-white/5 border border-white/10 rounded-md p-3">
                {t.header_text && <p className="text-sm font-bold text-white mb-1">{t.header_text}</p>}
                <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">{t.body_text || "—"}</p>
                {t.footer_text && <p className="text-xs text-slate-400 mt-2">{t.footer_text}</p>}
                {t.buttons && t.buttons.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {t.buttons.map((b, i) => (
                      <span key={i} className="text-[11px] font-semibold text-orange-400 bg-[#0a0a0a] border border-orange-500/30 rounded px-2 py-0.5">
                        {b.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-3">{t.variable_count} variable(s)</p>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent data-testid="create-template-dialog" className="w-[96vw] max-w-[1200px] sm:max-w-[1200px] max-h-[94vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Template</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8">
          <div className="space-y-5 py-2 max-h-[74vh] overflow-y-auto pr-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input data-testid="template-form-name" className={`mt-1.5 ${inputCls}`} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="renewal_reminder" />
              </div>
              <div>
                <label className={labelCls}>Language</label>
                <input data-testid="template-form-language" className={`mt-1.5 ${inputCls}`} value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })} placeholder="en" />
              </div>
            </div>

            <div>
              <label className={labelCls}>Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="template-form-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`category-opt-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-white/10 rounded-md p-3">
              <label className={labelCls}>Header (optional)</label>
              <Select value={form.header_type} onValueChange={(v) => setForm({ ...form, header_type: v })}>
                <SelectTrigger data-testid="template-form-header-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HEADER_TYPES.map((h) => (
                    <SelectItem key={h} value={h}>{h === "NONE" ? "None" : h.charAt(0) + h.slice(1).toLowerCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {form.header_type === "TEXT" && (
                <input data-testid="template-form-header-text" className={`mt-2 ${inputCls}`} value={form.header_text}
                  onChange={(e) => setForm({ ...form, header_text: e.target.value })} placeholder="Header text" />
              )}

              {form.header_type === "IMAGE" && (
                <div className="mt-2">
                  {headerImagePreview && (
                    <img src={headerImagePreview} alt="header preview" className="w-full max-h-64 object-contain rounded mb-2 bg-black/30" />
                  )}
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md py-2.5 cursor-pointer hover:border-orange-400 transition-all duration-150">
                    {uploadingHeaderImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4 text-slate-400" />}
                    <span className="text-sm text-slate-300">{uploadingHeaderImage ? "Uploading..." : "Upload image"}</span>
                    <input data-testid="template-header-image-file" type="file" accept="image/*" className="hidden" onChange={onHeaderImageFile} />
                  </label>
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>Body Text</label>
              <textarea data-testid="template-form-body" rows={10} className={`mt-1.5 ${inputCls} resize-none`} value={form.body_text}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                placeholder="Hi {{1}}, your membership expires on {{2}}." />
              <p className="text-xs text-slate-400 mt-1">Use {"{{1}}"}, {"{{2}}"} for variables.</p>
            </div>

            {samples.length > 0 && (
              <div>
                <label className={labelCls}>Sample Values</label>
                <div className="mt-1.5 space-y-2">
                  {samples.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 w-10">{`{{${i + 1}}}`}</span>
                      <input data-testid={`template-sample-${i}`} className={inputCls} value={s}
                        onChange={(e) => setSamples(samples.map((x, j) => (j === i ? e.target.value : x)))}
                        placeholder={`Example for variable ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Footer (optional)</label>
              <input data-testid="template-form-footer" className={`mt-1.5 ${inputCls}`} value={form.footer_text}
                onChange={(e) => setForm({ ...form, footer_text: e.target.value })} placeholder="Adiyash Gym" maxLength={60} />
            </div>

            <div className="border border-white/10 rounded-md p-3">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Buttons (optional, max 3)</label>
                <div className="flex gap-1.5">
                  <button type="button" className="text-xs font-bold text-orange-600 hover:underline" onClick={() => addButton("QUICK_REPLY")}>
                    + Quick Reply
                  </button>
                  <button type="button" className="text-xs font-bold text-orange-600 hover:underline" onClick={() => addButton("URL")}>
                    + Visit Website
                  </button>
                </div>
              </div>

              {buttons.length > 0 && (
                <div className="mt-2 space-y-2">
                  {buttons.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase text-slate-400 w-20 shrink-0">{b.type === "URL" ? "Website" : "Quick Reply"}</span>
                      <input className={inputCls} value={b.text} placeholder="Button text"
                        onChange={(e) => updateButton(i, { text: e.target.value })} />
                      {b.type === "URL" && (
                        <input className={inputCls} value={b.url} placeholder="https://..."
                          onChange={(e) => updateButton(i, { url: e.target.value })} />
                      )}
                      <button type="button" onClick={() => removeButton(i)} className="text-slate-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="py-2 max-h-[74vh] overflow-y-auto">
            <TemplatePreview form={form} samples={samples} imageUrl={headerImagePreview} buttons={buttons} />
          </div>
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setOpen(false)}>Cancel</button>
            <button data-testid="submit-template-button" className={primaryBtn} onClick={submit}
              disabled={saving || !form.name.trim() || !form.body_text.trim()}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit for Approval
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditTarget(null); }}>
        <DialogContent data-testid="edit-template-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Template{editTarget ? ` — ${editTarget.name}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <p className="text-xs text-slate-500">
              Only body text and footer can be edited here. Header type, name, and buttons cannot be
              changed on an existing template — create a new template for those changes.
              Editing will send the template back to Meta for re-approval.
            </p>

            <div>
              <label className={labelCls}>Body Text</label>
              <textarea data-testid="edit-template-body" rows={4} className={`mt-1.5 ${inputCls} resize-none`} value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                placeholder="Hi {{1}}, your membership expires on {{2}}." />
              <p className="text-xs text-slate-400 mt-1">Use {"{{1}}"}, {"{{2}}"} for variables.</p>
            </div>

            {editSamples.length > 0 && (
              <div>
                <label className={labelCls}>Sample Values</label>
                <div className="mt-1.5 space-y-2">
                  {editSamples.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 w-10">{`{{${i + 1}}}`}</span>
                      <input data-testid={`edit-template-sample-${i}`} className={inputCls} value={s}
                        onChange={(e) => setEditSamples(editSamples.map((x, j) => (j === i ? e.target.value : x)))}
                        placeholder={`Example for variable ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Footer (optional)</label>
              <input data-testid="edit-template-footer" className={`mt-1.5 ${inputCls}`} value={editFooter}
                onChange={(e) => setEditFooter(e.target.value)} placeholder="Adiyash Gym" maxLength={60} />
            </div>
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setEditOpen(false)}>Cancel</button>
            <button data-testid="save-edit-template-button" className={primaryBtn} onClick={submitEdit}
              disabled={editSaving || !editBody.trim()}>
              {editSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-template-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Template?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300 py-2">
            This will permanently delete{" "}
            <span className="font-bold">{deleteTarget?.name}</span> from Meta and from this app.
            Any automation using this template will stop working. This cannot be undone.
          </p>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button
              data-testid="confirm-delete-template-button"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}